import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tradeLicenseApi, getMediaUrl } from '../../../services/tradeLicenseApi';
import DocumentPreview from '../../../components/TradeLicense/DocumentPreview';
import './ApplicationDetail.css';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Workflow pipeline definition (for the progress bar)                         */
/* ─────────────────────────────────────────────────────────────────────────── */
const PIPELINE = [
  { status: 'draft',       label: 'Draft',       icon: '📝' },
  { status: 'submitted',   label: 'Submitted',   icon: '📨' },
  { status: 'in_progress', label: 'Verification',icon: '🔍' },
  { status: 'inspection',  label: 'Inspection',  icon: '🏢' },
  { status: 'approved',    label: 'Approved',    icon: '✅' },
  { status: 'paid',        label: 'Payment',     icon: '💰' },
  { status: 'issued',      label: 'Issued',      icon: '🏆' },
];

const PIPELINE_IDX = Object.fromEntries(PIPELINE.map((s, i) => [s.status, i]));

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Helper — get human label for any status                                     */
/* ─────────────────────────────────────────────────────────────────────────── */
const STATUS_LABELS = {
  draft: 'Draft', submitted: 'Submitted', in_progress: 'In Progress',
  verification: 'Verification', approved: 'Approved', rejected: 'Rejected',
  paid: 'Payment Completed', issued: 'License Issued', cancelled: 'Cancelled',
  inspection: 'Inspection',
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  WorkflowBar                                                                 */
/* ─────────────────────────────────────────────────────────────────────────── */
const WorkflowBar = ({ status, stages = [], currentStageId }) => {
  const activeStages = stages.filter((stage) => stage.is_active !== false);
  const dynamicStages = activeStages.length
    ? activeStages.map((stage) => ({
      id: stage.id,
      status: stage.stage_type,
      label: stage.name_en,
      icon: stage.is_terminal_state ? '■' : '●',
    }))
    : PIPELINE;
  const cur = activeStages.length
    ? dynamicStages.findIndex((stage) => String(stage.id) === String(currentStageId))
    : (PIPELINE_IDX[status] ?? -1);
  const rejected = status === 'rejected' || status === 'cancelled';

  return (
    <div className={`workflow-bar ${rejected ? 'rejected' : ''}`}>
      {dynamicStages.map((step, idx) => {
        const done    = idx < cur;
        const active  = idx === cur;
        const pending = idx > cur;
        return (
          <React.Fragment key={step.id || step.status}>
            <div className={`wf-step ${done ? 'done' : ''} ${active ? 'active' : ''} ${pending ? 'pending' : ''}`}>
              <div className="wf-circle">{done ? '✓' : step.icon}</div>
              <span className="wf-label">{step.label}</span>
            </div>
            {idx < dynamicStages.length - 1 && (
              <div className={`wf-line ${done ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
      {rejected && (
        <div className="wf-rejected-badge">❌ {STATUS_LABELS[status] || status}</div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main Component                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */
const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [application, setApplication]   = useState(null);
  const [documents, setDocuments]       = useState([]);
  const [inspectors, setInspectors]     = useState([]);
  const [workflowStages, setWorkflowStages] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  /* Modal/action state */
  const [modal, setModal]               = useState(null);
  const [previewDoc, setPreviewDoc]     = useState(null);
  const [reasonText, setReasonText]     = useState('');
  const [inspectorId, setInspectorId]   = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentRemarks, setPaymentRemarks] = useState('');

  /* ── Data fetch ────────────────────────────────────────────────────────── */
  const fetchDetail = useCallback(async () => {
    if (id === 'new') return;
    try {
      const [appRes, docsRes, wfRes] = await Promise.all([
        tradeLicenseApi.getApplication(id),
        tradeLicenseApi.getApplicationDocuments(id),
        tradeLicenseApi.getWorkflowStages(),
      ]);
      setApplication(appRes.data);
      setDocuments(docsRes.data.results || docsRes.data || []);
      setWorkflowStages(wfRes.data.results || wfRes.data || []);

    } catch (err) {
      console.error('Error fetching application:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  useEffect(() => {
    tradeLicenseApi.getAssignableInspectors()
      .then((r) => setInspectors(r.data.results || r.data || []))
      .catch(() => {});
  }, []);

  /* ── Action handlers ───────────────────────────────────────────────────── */
  const closeModal = () => {
    setModal(null);
    setReasonText('');
    setInspectorId('');
    setScheduledDate('');
    setPaymentAmount('');
    setPaymentMethod('cash');
    setPaymentReference('');
    setPaymentRemarks('');
  };

  const openPaymentModal = () => {
    if (!application) return;
    const balance = Math.max(0, parseFloat(application.application_fee) - parseFloat(application.paid_amount || 0));
    setPaymentAmount(balance > 0 ? String(balance) : '');
    setPaymentMethod('cash');
    setPaymentReference('');
    setPaymentRemarks('');
    setModal('payment');
  };

  const autoTranslate = async (text) => {
    try {
      const r = await tradeLicenseApi.translateText(text, { field_context: 'remarks' });
      return r.data.translated || text;
    } catch { return text; }
  };

  const handleConfirm = async () => {
    if (!modal) return;
    setActionLoading(true);
    try {
      switch (modal) {
        case 'verify':
          await tradeLicenseApi.beginVerification(id);
          break;

        case 'approve':
          await tradeLicenseApi.approveApplication(id, { comment: reasonText });
          break;

        case 'issue':
          await tradeLicenseApi.issueLicense(id);
          break;

        case 'reject': {
          if (!reasonText.trim()) { alert('Please enter a rejection reason.'); return; }
          const hiReject = await autoTranslate(reasonText);
          await tradeLicenseApi.rejectApplication(id, { reason_en: reasonText, reason_hi: hiReject });
          break;
        }

        case 'clarification': {
          if (!reasonText.trim()) { alert('Please enter the clarification note.'); return; }
          const hiClarify = await autoTranslate(reasonText);
          await tradeLicenseApi.clarificationApplication(id, { reason_en: reasonText, reason_hi: hiClarify });
          break;
        }

        case 'inspect': {
          if (!inspectorId || !scheduledDate) {
            alert('Please select an inspector and a scheduled date.');
            return;
          }
          await tradeLicenseApi.assignInspection(id, {
            inspector_id: inspectorId,
            scheduled_date: scheduledDate,
          });
          break;
        }

        case 'payment': {
          if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
            alert('Please enter a valid payment amount.');
            return;
          }
          if (paymentMethod === 'cheque' && !paymentReference.trim()) {
            alert('Please enter the cheque number.');
            return;
          }
          await tradeLicenseApi.recordPayment(id, {
            amount: paymentAmount,
            payment_method: paymentMethod,
            reference: paymentReference,
            remarks: paymentRemarks,
          });
          break;
        }

        default:
          break;
      }

      closeModal();
      fetchDetail();
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.error
        || (typeof data === 'object' ? Object.values(data).flat().join(' ') : null)
        || err.message;
      alert(`Error: ${msg}`);
    } finally {
      setActionLoading(false);
    }
  };

  /* ── Document verify ───────────────────────────────────────────────────── */
  const handleVerifyDocument = async (docId, newStatus = 'verified') => {
    try {
      await tradeLicenseApi.verifyDocument(docId, { status: newStatus });
      fetchDetail();
    } catch (err) {
      alert('Error verifying document: ' + err.message);
    }
  };

  /* ── Loading / not found ───────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="app-detail-loading">
        <div className="spinner" />
        <p>Loading application…</p>
      </div>
    );
  }

  if (!application) {
    return (
      <div>
        <h1 className="page-title">Application Not Found</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/office/applications')}>
          ← Back to Applications
        </button>
      </div>
    );
  }

  const { status } = application;
  const isTerminal = ['issued', 'cancelled'].includes(status);
  const pendingPaymentAmount = parseFloat(application.pending_payment_amount || 0);
  const balanceDue = Math.max(
    0,
    parseFloat(application.application_fee || 0)
      - parseFloat(application.paid_amount || 0)
      - pendingPaymentAmount
  );
  const activeWorkflowStages = workflowStages.filter((stage) => stage.is_active !== false);
  const currentStage = activeWorkflowStages.find((stage) => String(stage.id) === String(application.current_stage));
  const canCollectPayment = !isTerminal && status !== 'rejected' && balanceDue > 0
    && ['approved', 'paid'].includes(status);

  const currentStageType = currentStage?.stage_type || status;
  const isWorkflowActionStage = currentStage?.requires_officer_action
    && !['payment', 'issued', 'rejected', 'cancelled'].includes(currentStageType);
  const canApproveCurrentLevel = isWorkflowActionStage;

  /* ── Render ────────────────────────────────────────────────────────────── */
  return (
    <div className="app-detail-container">

      {/* Back */}
      <button className="btn btn-secondary back-btn" onClick={() => navigate('/office/applications')}>
        ← Back to Applications
      </button>

      {/* Header */}
      <div className="app-detail-header">
        <div className="app-detail-header-info">
          <h1>#{application.application_number}</h1>
          <p>{application.business_name_en}</p>
          {application.business_name_hi && (
            <p className="text-hindi">{application.business_name_hi}</p>
          )}
        </div>
        <span className={`status-badge status-${status}`}>
          {STATUS_LABELS[status] || status}
        </span>
      </div>

      {status === 'issued' && application.license_number && (
        <div className="card issued-certificate-card">
          <div>
            <h3>Trade License Issued</h3>
            <p>License No: <strong>{application.license_number}</strong></p>
          </div>
          <button
            type="button"
            className="btn btn-success"
            onClick={() => navigate(`/office/licenses/certificate/${application.license_number}`)}
          >
            View Certificate
          </button>
        </div>
      )}

      {/* Workflow Progress Bar */}
      <div className="card">
        <h3 className="section-title">📋 Application Workflow</h3>
        <WorkflowBar
          status={status}
          stages={workflowStages}
          currentStageId={application.current_stage}
        />
        {status === 'rejected' && application.rejection_reason_en && (
          <div className="rejection-reason">
            <strong>Rejection Reason:</strong> {application.rejection_reason_en}
          </div>
        )}
        {application.clarification_required && application.clarification_reason_en && (
          <div className="clarification-reason">
            <strong>Clarification Requested:</strong> {application.clarification_reason_en}
          </div>
        )}
      </div>

      {/* ── Action Panel ─────────────────────────────────────────────────── */}
      {!isTerminal && status !== 'rejected' && (
        <div className="card actions-card">
          <h3 className="section-title">⚡ Actions</h3>
          <div className="action-buttons">

            {status === 'submitted' && !currentStage?.requires_officer_action && (
              <>
                <button className="btn btn-primary" onClick={() => setModal('verify')}>
                  ✅ Move to Verification
                </button>
                <button className="btn btn-secondary" onClick={() => setModal('clarification')}>
                  💬 Request Clarification
                </button>
                <button className="btn btn-danger" onClick={() => setModal('reject')}>
                  ❌ Reject
                </button>
              </>
            )}

            {canApproveCurrentLevel && (
              <>
                <button className="btn btn-primary" onClick={() => setModal('approve')}>
                  ✅ Approve Current Level
                </button>
                <button className="btn btn-secondary" onClick={() => setModal('clarification')}>
                  💬 Request Clarification
                </button>
                <button className="btn btn-danger" onClick={() => setModal('reject')}>
                  ❌ Reject
                </button>
              </>
            )}

            {(status === 'approved' || status === 'paid') && (
              <>
                {canCollectPayment && (
                  <button className="btn btn-primary" onClick={openPaymentModal}>
                    💰 Collect Payment
                  </button>
                )}
                <button
                  className="btn btn-success"
                  onClick={() => setModal('issue')}
                  disabled={balanceDue > 0}
                  title={balanceDue > 0 ? 'Collect full payment before issuing license' : ''}
                >
                  🏆 Issue License
                </button>
                <button className="btn btn-danger" onClick={() => setModal('reject')}>
                  ❌ Reject
                </button>
              </>
            )}

            {!isWorkflowActionStage && !['approved', 'paid', 'submitted'].includes(status) && (
              <p className="action-hint">No officer action is configured for this stage.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Detail Grid ──────────────────────────────────────────────────── */}
      <div className="detail-grid">

        {/* Applicant Info */}
        <div className="detail-card">
          <h3>👤 Applicant Information</h3>
          {application.applicant_photograph_url && (
            <div className="applicant-photo-wrap">
              <img
                src={getMediaUrl(application.applicant_photograph_url)}
                alt="Applicant"
                className="applicant-photo"
              />
            </div>
          )}
          <dl className="detail-list">
            <dt>Name (EN):</dt>
            <dd>{application.applicant_first_name_en} {application.applicant_last_name_en}</dd>
            {application.applicant_first_name_hi && (
              <><dt>Name (HI):</dt><dd className="text-hindi">{application.applicant_first_name_hi} {application.applicant_last_name_hi}</dd></>
            )}
            {application.applicant_father_husband_name_en && (
              <><dt>Father/Husband:</dt><dd>{application.applicant_father_husband_name_en}</dd></>
            )}
            <dt>Email:</dt>  <dd>{application.applicant_email}</dd>
            <dt>Phone:</dt>  <dd>{application.applicant_phone}</dd>
            {application.applicant_aadhar && <><dt>Aadhaar:</dt><dd>XXXX-XXXX-{application.applicant_aadhar.slice(-4)}</dd></>}
            {application.applicant_pan    && <><dt>PAN:</dt><dd>{application.applicant_pan}</dd></>}
            {application.applicant_gender && <><dt>Gender:</dt><dd>{application.applicant_gender}</dd></>}
          </dl>
        </div>

        {/* Business Info */}
        <div className="detail-card">
          <h3>🏢 Business Information</h3>
          <dl className="detail-list">
            <dt>Business (EN):</dt>   <dd>{application.business_name_en}</dd>
            <dt>Business (HI):</dt>   <dd>{application.business_name_hi || '—'}</dd>
            <dt>Trade Category:</dt>  <dd>{application.trade_category_name}</dd>
            <dt>Business Type:</dt>   <dd>{application.business_type}</dd>
            <dt>Ownership:</dt>       <dd>{application.ownership_type || '—'}</dd>
            <dt>GST:</dt>             <dd>{application.gst_number || '—'}</dd>
            <dt>Ward:</dt>            <dd>{application.ward_number || '—'}</dd>
            <dt>Holding No:</dt>      <dd>{application.holding_number || '—'}</dd>
            <dt>Address (EN):</dt>    <dd>{application.address_en}</dd>
            {application.address_hi && <><dt>Address (HI):</dt><dd className="text-hindi">{application.address_hi}</dd></>}
            {application.existing_license_number && (
              <><dt>Existing License:</dt><dd>{application.existing_license_number}</dd></>
            )}
            {application.property_owner_name_en && (
              <><dt>Property Owner:</dt><dd>{application.property_owner_name_en}</dd></>
            )}
            {application.property_owner_address_en && (
              <><dt>Owner Address:</dt><dd>{application.property_owner_address_en}</dd></>
            )}
          </dl>
        </div>

        {/* Workflow Status */}
        <div className="detail-card">
          <h3>🔄 Workflow Status</h3>
          <dl className="detail-list">
            <dt>Current Stage:</dt>
            <dd>{application.current_stage_name || '—'}</dd>
            <dt>Assigned To:</dt>
            <dd>{application.assigned_to_name || application.assigned_to_username || 'Unassigned'}</dd>
            <dt>Status:</dt>
            <dd><span className={`status-badge status-${status}`}>{STATUS_LABELS[status] || status}</span></dd>
            {status === 'issued' && application.license_number && (
              <><dt>License No:</dt><dd><strong style={{ color: '#28a745', fontSize: '1.1em' }}>{application.license_number}</strong></dd></>
            )}
            <dt>Payment Status:</dt>
            <dd><span className={`status-badge status-${application.payment_status}`}>{application.payment_status}</span></dd>
            <dt>Application Type:</dt>
            <dd>{application.application_type?.replace(/_/g, ' ')}</dd>
            {application.submitted_date && (
              <><dt>Submitted:</dt><dd>{new Date(application.submitted_date).toLocaleString()}</dd></>
            )}
            {application.approved_date && (
              <><dt>Approved:</dt><dd>{new Date(application.approved_date).toLocaleString()}</dd></>
            )}
            <dt>Last Updated:</dt>
            <dd>{new Date(application.updated_at).toLocaleString()}</dd>
          </dl>
        </div>

        {/* Payment */}
        <div className="detail-card">
          <h3>💰 Payment Information</h3>
          <dl className="detail-list">
            <dt>Application Fee:</dt> <dd>₹{application.application_fee}</dd>
            <dt>Paid Amount:</dt>     <dd>₹{application.paid_amount}</dd>
            {pendingPaymentAmount > 0 && (
              <><dt>Pending Validation:</dt><dd>₹{pendingPaymentAmount.toFixed(2)}</dd></>
            )}
            <dt>Balance Due:</dt>
            <dd style={{ color: balanceDue > 0 ? '#cc0000' : '#009900' }}>
              ₹{balanceDue.toFixed(2)}
            </dd>
            <dt>Payment Status:</dt>
            <dd><span className={`status-badge status-${application.payment_status}`}>{application.payment_status}</span></dd>
          </dl>
          {canCollectPayment && (
            <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={openPaymentModal}>
              💰 Collect Payment (₹{balanceDue.toFixed(2)} due)
            </button>
          )}
        </div>

        {/* Inspection Details */}
        {application.inspections && application.inspections.length > 0 && (
          <div className="detail-card full-width">
            <h3>🔍 Inspection Details</h3>
            <div className="inspections-list">
              {application.inspections.map((insp, idx) => (
                <div key={insp.id || idx} className="inspection-item" style={{ padding: '15px', border: '1px solid #eee', borderRadius: '6px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                    <strong style={{ fontSize: '1.1em' }}>Inspector: {insp.assigned_to_name || 'Unassigned'}</strong>
                    <span className={`status-badge status-${insp.status}`}>{insp.status}</span>
                  </div>
                  <dl className="detail-list" style={{ gridTemplateColumns: '150px 1fr', gap: '8px' }}>
                    <dt>Scheduled:</dt>
                    <dd>{insp.scheduled_date ? new Date(insp.scheduled_date).toLocaleString() : 'Not scheduled'} {insp.scheduled_time_slot && `(${insp.scheduled_time_slot})`}</dd>
                    {insp.visited_date && <><dt>Visited:</dt><dd>{new Date(insp.visited_date).toLocaleString()}</dd></>}
                    <dt>Recommendation:</dt>
                    <dd>{insp.recommendation ? insp.recommendation.replace('_', ' ').toUpperCase() : 'Pending'}</dd>
                    <dt>Remarks:</dt>
                    <dd>{insp.recommendation_remarks_en || 'None'}</dd>
                  </dl>
                  {insp.checklist && insp.checklist.length > 0 && (
                    <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px' }}>Checklist:</strong>
                      <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
                        {insp.checklist.map((item, i) => (
                          <li key={i} style={{ marginBottom: '6px' }}>
                            {item.compliant ? '✅' : '❌'} <span style={{ fontWeight: '500' }}>{item.item}</span>
                            {item.remarks && <span style={{ color: '#666', marginLeft: '8px', fontSize: '0.9em' }}>- {item.remarks}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approval History */}
        {application.approval_history && application.approval_history.length > 0 && (
          <div className="detail-card full-width">
            <h3>Approval Comments &amp; History</h3>
            <div className="approval-history-list">
              {application.approval_history.map((entry) => (
                <div key={entry.id} className="approval-history-item">
                  <div className="approval-history-head">
                    <strong>{entry.action.replace(/_/g, ' ')}</strong>
                    <span>{entry.performed_date ? new Date(entry.performed_date).toLocaleString() : ''}</span>
                  </div>
                  <div className="approval-history-meta">
                    By {entry.performed_by_name || 'System'}
                    {entry.new_values?.next_stage && ` -> ${entry.new_values.next_stage}`}
                    {entry.new_values?.assigned_to && ` (assigned to ${entry.new_values.assigned_to})`}
                  </div>
                  {entry.remarks && <p>{entry.remarks}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents */}
        <div className="detail-card full-width">
          <h3>📄 Documents ({documents.length})</h3>
          {documents.length > 0 ? (
            <div className="documents-list">
              {documents.map(doc => (
                <div key={doc.id} className="document-item">
                  <div className="document-info">
                    <strong>{doc.document_type?.replace(/_/g, ' ')}</strong>
                    <p>{doc.name_en || doc.name_hi}</p>
                    <span className={`status-badge status-${doc.verification_status}`}>
                      {doc.verification_status}
                    </span>
                  </div>
                  <div className="document-actions">
                    {doc.verification_status !== 'verified' && (
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleVerifyDocument(doc.id, 'verified')}
                      >
                        ✅ Verify
                      </button>
                    )}
                    {doc.verification_status !== 'rejected' && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleVerifyDocument(doc.id, 'rejected')}
                      >
                        ❌ Reject Doc
                      </button>
                    )}
                    {(doc.file || doc.file_url) && (
                      <>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setPreviewDoc(doc)}
                        >
                          👁 View
                        </button>
                        <a
                          href={getMediaUrl(doc.file_url || doc.file)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm"
                        >
                          ⬇️ Download
                        </a>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-docs">No documents uploaded yet.</p>
          )}
        </div>

      </div>{/* /detail-grid */}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/*  Action Modal                                                        */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>

            {/* Title */}
            <div className="modal-header">
              <h2>
                {modal === 'verify'        && '✅ Move to Verification'}
                {modal === 'approve'       && '✅ Approve Application'}
                {modal === 'issue'         && '🏆 Issue License'}
                {modal === 'reject'        && '❌ Reject Application'}
                {modal === 'clarification' && '💬 Request Clarification'}
                {modal === 'inspect'       && '🔍 Assign Inspection'}
                {modal === 'payment'       && '💰 Collect Payment'}
              </h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            {modal === 'verify' && (
              <p className="modal-desc">
                This will move the application to the document verification stage for officer review.
              </p>
            )}

            {modal === 'approve' && (
              <>
                <p className="modal-desc">
                  This will approve the current workflow level and move the application to the next configured level.
                </p>
                <div className="form-group">
                  <label>Approval Comment (optional)</label>
                  <textarea
                    value={reasonText}
                    onChange={e => setReasonText(e.target.value)}
                    placeholder="Add a comment for this approval level..."
                    rows="3"
                    className="modal-textarea"
                  />
                </div>
              </>
            )}

            {modal === 'issue' && (
              <p className="modal-desc">
                This will issue the trade license and generate a certificate for the applicant.
              </p>
            )}

            {/* Reject / Clarification */}
            {(modal === 'reject' || modal === 'clarification') && (
              <div className="form-group">
                <label>
                  {modal === 'reject'
                    ? 'Rejection Reason (English)'
                    : 'Clarification Note (English)'}
                  <span className="req"> *</span>
                </label>
                <textarea
                  value={reasonText}
                  onChange={e => setReasonText(e.target.value)}
                  placeholder={
                    modal === 'reject'
                      ? 'Enter clear reason for rejection…'
                      : 'Describe what additional information is needed…'
                  }
                  rows="4"
                  className="modal-textarea"
                />
                <small>Hindi translation will be auto-generated.</small>
              </div>
            )}

            {modal === 'payment' && (
              <>
                <p className="modal-desc">
                  Balance due: <strong>₹{balanceDue.toFixed(2)}</strong> of ₹{parseFloat(application.application_fee || 0).toFixed(2)} total fee.
                </p>
                <div className="form-group">
                  <label>Amount (₹) <span className="req">*</span></label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={balanceDue}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="modal-input"
                  />
                </div>
                <div className="form-group">
                  <label>Payment Mode <span className="req">*</span></label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="modal-select"
                  >
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="transfer">Bank Transfer</option>
                    <option value="online" disabled>Online Payment (Coming Soon)</option>
                  </select>
                </div>
                {(paymentMethod === 'cheque' || paymentMethod === 'transfer') && (
                  <div className="form-group">
                    <label>
                      {paymentMethod === 'cheque' ? 'Cheque Number' : 'Transaction Reference'}
                      {paymentMethod === 'cheque' && <span className="req"> *</span>}
                    </label>
                    <input
                      type="text"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      className="modal-input"
                      placeholder={paymentMethod === 'cheque' ? 'Enter cheque number' : 'UTR / reference number'}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Remarks (optional)</label>
                  <textarea
                    value={paymentRemarks}
                    onChange={(e) => setPaymentRemarks(e.target.value)}
                    rows="2"
                    className="modal-textarea"
                    placeholder="Any additional notes…"
                  />
                </div>
              </>
            )}

            {/* Assign Inspection */}
            {modal === 'inspect' && (
              <>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label>Select Inspector <span className="req">*</span></label>
                  <select
                    value={inspectorId}
                    onChange={e => setInspectorId(e.target.value)}
                    className="modal-select"
                  >
                    <option value="">— Choose an inspector —</option>
                    {inspectors.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.user_name || r.user_username || r.user_email} ({r.role.replace(/_/g, ' ')})
                      </option>
                    ))}
                  </select>
                  {inspectors.length === 0 && (
                    <small style={{ color: '#cc6600' }}>
                      No inspectors found. Add inspector users in User Management first.
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Scheduled Date &amp; Time <span className="req">*</span></label>
                  <input
                    type="datetime-local"
                    value={scheduledDate}
                    onChange={e => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="modal-input"
                  />
                </div>
              </>
            )}

            {/* Buttons */}
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={closeModal}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className={`btn ${modal === 'reject' ? 'btn-danger' : modal === 'issue' ? 'btn-success' : 'btn-primary'}`}
                onClick={handleConfirm}
                disabled={
                  actionLoading ||
                  ((modal === 'reject' || modal === 'clarification') && !reasonText.trim()) ||
                  (modal === 'inspect' && (!inspectorId || !scheduledDate)) ||
                  (modal === 'payment' && (!paymentAmount || parseFloat(paymentAmount) <= 0
                    || (paymentMethod === 'cheque' && !paymentReference.trim())))
                }
              >
                {actionLoading ? 'Processing…' : 'Confirm'}
              </button>
            </div>

          </div>
        </div>
      )}

      {previewDoc && (
        <DocumentPreview doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}

    </div>
  );
};

export default ApplicationDetail;